import { useEffect, useMemo, useState } from "react";
import { BookOpen, Check, Copy, Menu, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { clsx } from "clsx";
import {
  isTauriEnvironment,
  KNOWLEDGE_CATEGORIES,
  KNOWLEDGE_CATEGORY_LABELS,
  type CreateKnowledgeInput,
  type KnowledgeCategory,
  type KnowledgeItem,
} from "../lib/tauri";
import { collectTags, splitTags } from "../lib/knowledgeCore";
import { useKnowledgeStore } from "../stores/knowledgeStore";
import { useUiStore } from "../stores/uiStore";
import { Button } from "./ui/Button";
import { GlobalSearchButton } from "./GlobalSearch";

const TITLE_PLACEHOLDER = "例: Opusに根本原因を3行で説明させるプロンプト";

interface FormState {
  id: string | null;
  title: string;
  body: string;
  tags: string;
  category: KnowledgeCategory;
  usefulness: number;
}

const EMPTY_FORM: FormState = {
  id: null,
  title: "",
  body: "",
  tags: "",
  category: "prompt",
  usefulness: 0,
};

/** 役立ち度の星（読み取り or クリックで評価） */
function StarRating({
  value,
  onRate,
}: {
  value: number;
  onRate?: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onRate}
          aria-label={`役立ち度 ${n}`}
          onClick={() => onRate?.(n === value ? 0 : n)}
          className={clsx(
            "transition",
            onRate ? "cursor-pointer hover:scale-110" : "cursor-default",
          )}
        >
          <Star
            className={clsx(
              "h-4 w-4",
              n <= value
                ? "fill-amber-400 text-amber-400"
                : "text-muted dark:text-muted-dark",
            )}
          />
        </button>
      ))}
    </div>
  );
}

export function KnowledgeView({ onMenuClick }: { onMenuClick?: () => void }) {
  const items = useKnowledgeStore((s) => s.items);
  const loaded = useKnowledgeStore((s) => s.loaded);
  const search = useKnowledgeStore((s) => s.search);
  const category = useKnowledgeStore((s) => s.category);
  const tag = useKnowledgeStore((s) => s.tag);
  const load = useKnowledgeStore((s) => s.load);
  const setSearch = useKnowledgeStore((s) => s.setSearch);
  const setCategory = useKnowledgeStore((s) => s.setCategory);
  const setTag = useKnowledgeStore((s) => s.setTag);
  const create = useKnowledgeStore((s) => s.create);
  const update = useKnowledgeStore((s) => s.update);
  const remove = useKnowledgeStore((s) => s.remove);
  const rate = useKnowledgeStore((s) => s.rate);
  const touchUsed = useKnowledgeStore((s) => s.touchUsed);
  const showToast = useUiStore((s) => s.showToast);

  const [form, setForm] = useState<FormState | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 初回ロード + 外部（HTTP/MCP）書き込みの即時反映（GoalsView の goals-changed と同型）
  useEffect(() => {
    void load();
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://knowledge-changed", () => {
        void load();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      }),
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [load]);

  // 一覧に出ている全タグ（チップ絞り込み用・重複排除）
  const allTags = useMemo(() => collectTags(items), [items]);

  const handleCopy = async (item: KnowledgeItem) => {
    try {
      await navigator.clipboard.writeText(item.body);
      setCopiedId(item.id);
      window.setTimeout(() => setCopiedId((c) => (c === item.id ? null : c)), 1500);
      // コピー = 使った扱い（last_used_at 更新）
      void touchUsed(item.id);
    } catch {
      showToast("クリップボードにコピーできませんでした");
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    const title = form.title.trim();
    if (!title) {
      showToast("タイトルを入力してください");
      return;
    }
    try {
      if (form.id) {
        await update(form.id, {
          title,
          body: form.body,
          tags: form.tags,
          category: form.category,
          usefulness: form.usefulness,
        });
        showToast("ナレッジを更新しました");
      } else {
        const input: CreateKnowledgeInput = {
          title,
          body: form.body,
          tags: form.tags,
          category: form.category,
          usefulness: form.usefulness,
          source: "manual",
        };
        await create(input);
        showToast("ナレッジを保存しました");
      }
      setForm(null);
    } catch {
      showToast("保存に失敗しました（Flow アプリ起動中のみ書き込めます）");
    }
  };

  const handleDelete = async (item: KnowledgeItem) => {
    if (!window.confirm(`「${item.title}」を削除しますか？`)) return;
    try {
      await remove(item.id);
      showToast("削除しました");
    } catch {
      showToast("削除に失敗しました");
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3 dark:border-border-dark">
        <button
          type="button"
          aria-label="メニュー"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="flex items-center gap-2 text-base font-bold">
          <BookOpen className="h-4 w-4" /> ナレッジ
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setForm({ ...EMPTY_FORM })}
          >
            追加
          </Button>
          <GlobalSearchButton />
        </div>
      </header>

      {/* 検索 + カテゴリ絞り込み */}
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3 dark:border-border-dark">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="タイトル・本文・タグで検索"
          className="h-9 w-full rounded-md border border-border bg-bg px-3 text-sm outline-none focus:border-brand-500 dark:border-border-dark dark:bg-bg-dark"
        />
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setCategory(null)}
            className={clsx(
              "rounded-full px-2.5 py-1 text-xs font-medium transition",
              category === null
                ? "bg-brand-500 text-white"
                : "bg-hover text-muted hover:text-text dark:bg-hover-dark dark:text-muted-dark dark:hover:text-text-dark",
            )}
          >
            すべて
          </button>
          {KNOWLEDGE_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(category === c ? null : c)}
              className={clsx(
                "rounded-full px-2.5 py-1 text-xs font-medium transition",
                category === c
                  ? "bg-brand-500 text-white"
                  : "bg-hover text-muted hover:text-text dark:bg-hover-dark dark:text-muted-dark dark:hover:text-text-dark",
              )}
            >
              {KNOWLEDGE_CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {tag && (
              <button
                type="button"
                onClick={() => setTag(null)}
                className="flex items-center gap-1 rounded-full bg-brand-500/15 px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-200"
              >
                #{tag} <X className="h-3 w-3" />
              </button>
            )}
            {!tag &&
              allTags.slice(0, 12).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTag(t)}
                  className="rounded-full bg-hover px-2.5 py-1 text-xs text-muted hover:text-text dark:bg-hover-dark dark:text-muted-dark dark:hover:text-text-dark"
                >
                  #{t}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* 一覧 */}
      <div className="flow-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {!loaded ? (
          <p className="text-sm text-muted dark:text-muted-dark">読み込み中…</p>
        ) : items.length === 0 ? (
          <div className="mx-auto mt-12 max-w-sm text-center">
            <BookOpen className="mx-auto h-10 w-10 text-muted dark:text-muted-dark" />
            <p className="mt-3 text-sm font-semibold">ナレッジがまだありません</p>
            <p className="mt-1 text-xs text-muted dark:text-muted-dark">
              効いたプロンプト・AIの使い方・ツール知見を保存しておくと、後から検索・コピーして引き出せます。
            </p>
            <Button
              variant="primary"
              size="sm"
              className="mt-4"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setForm({ ...EMPTY_FORM })}
            >
              最初のナレッジを追加
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <article
                key={item.id}
                className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border-dark dark:bg-card-dark"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="min-w-0 break-words text-sm font-semibold">{item.title}</h3>
                  <span className="shrink-0 rounded-full bg-hover px-2 py-0.5 text-[10px] font-medium text-muted dark:bg-hover-dark dark:text-muted-dark">
                    {KNOWLEDGE_CATEGORY_LABELS[item.category]}
                  </span>
                </div>
                {item.body && (
                  <p className="line-clamp-4 whitespace-pre-wrap break-words text-xs text-muted dark:text-muted-dark">
                    {item.body}
                  </p>
                )}
                {splitTags(item.tags).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {splitTags(item.tags).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTag(t)}
                        className="rounded-full bg-hover px-2 py-0.5 text-[10px] text-muted hover:text-text dark:bg-hover-dark dark:text-muted-dark dark:hover:text-text-dark"
                      >
                        #{t}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between">
                  <StarRating value={item.usefulness} onRate={(n) => void rate(item.id, n)} />
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label="本文をコピー"
                      onClick={() => void handleCopy(item)}
                      className="flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-brand-700 hover:bg-brand-500/10 dark:text-brand-200"
                    >
                      {copiedId === item.id ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> コピー済み
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" /> コピー
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="編集"
                      onClick={() =>
                        setForm({
                          id: item.id,
                          title: item.title,
                          body: item.body,
                          tags: item.tags,
                          category: item.category,
                          usefulness: item.usefulness,
                        })
                      }
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="削除"
                      onClick={() => void handleDelete(item)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-danger-500/10 hover:text-danger-600 dark:text-muted-dark"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* 追加/編集モーダル */}
      {form && (
        <div className="fixed inset-0 z-40 flex items-stretch justify-center bg-stone-950/30 backdrop-blur-sm md:items-center md:p-6">
          <div className="flex w-full flex-col bg-card dark:bg-card-dark md:max-w-[520px] md:rounded-lg md:border md:border-border md:shadow-elevated md:dark:border-border-dark">
            <div className="flex items-center justify-between border-b border-border px-4 py-3 dark:border-border-dark">
              <h3 className="text-sm font-bold">
                {form.id ? "ナレッジを編集" : "ナレッジを追加"}
              </h3>
              <button
                type="button"
                aria-label="閉じる"
                onClick={() => setForm(null)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flow-scrollbar flex flex-col gap-3 overflow-y-auto px-4 py-4">
              <label className="flex flex-col gap-1 text-xs font-medium text-muted dark:text-muted-dark">
                タイトル
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={TITLE_PLACEHOLDER}
                  className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-text outline-none focus:border-brand-500 dark:border-border-dark dark:bg-bg-dark dark:text-text-dark"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-muted dark:text-muted-dark">
                本文（コピー対象）
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={6}
                  placeholder="プロンプト本文・手順・知見をそのまま貼り付け"
                  className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-brand-500 dark:border-border-dark dark:bg-bg-dark dark:text-text-dark"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-muted dark:text-muted-dark">
                タグ（カンマ区切り）
                <input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="debug, opus, claude-code"
                  className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-text outline-none focus:border-brand-500 dark:border-border-dark dark:bg-bg-dark dark:text-text-dark"
                />
              </label>
              <div className="flex flex-col gap-1 text-xs font-medium text-muted dark:text-muted-dark">
                カテゴリ
                <div className="flex flex-wrap gap-1.5">
                  {KNOWLEDGE_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, category: c })}
                      className={clsx(
                        "rounded-full px-2.5 py-1 text-xs font-medium transition",
                        form.category === c
                          ? "bg-brand-500 text-white"
                          : "bg-hover text-muted hover:text-text dark:bg-hover-dark dark:text-muted-dark dark:hover:text-text-dark",
                      )}
                    >
                      {KNOWLEDGE_CATEGORY_LABELS[c]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-muted dark:text-muted-dark">
                役立ち度
                <StarRating
                  value={form.usefulness}
                  onRate={(n) => setForm({ ...form, usefulness: n })}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 dark:border-border-dark">
              <Button variant="outline" size="md" onClick={() => setForm(null)}>
                キャンセル
              </Button>
              <Button variant="primary" size="md" onClick={() => void handleSubmit()}>
                {form.id ? "更新" : "保存"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
